import {
  AlignmentType,
  BorderStyle,
  Document,
  Header,
  ImageRun,
  Packer,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TextRun,
  VerticalAlign,
  WidthType,
} from "docx";
import tenfiftyLogoUrl from "@/assets/tenfifty-logo.png";
import { TERM_SHEET_DISCLAIMER } from "./termSheetConstants";
import {
  formatTermSheetLabel,
  getVisibleTermSheetSections,
  toTermSheetDownloadFilename,
} from "./termSheetHelpers";
import type { TermSheetSectionRow } from "./types";

const FONT = "Times New Roman";
const SIZE_TITLE = 24; // 12pt
const SIZE_BODY = 22; // 11pt

/** Matches the Porto term sheet header logo (~1.85" x 1.17"). */
const LOGO_WIDTH = 178;
const LOGO_HEIGHT = 112;

let logoDataPromise: Promise<Uint8Array> | null = null;

function loadTenfiftyLogoData(): Promise<Uint8Array> {
  if (!logoDataPromise) {
    logoDataPromise = fetch(tenfiftyLogoUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to load TenFifty logo");
        }
        return response.arrayBuffer();
      })
      .then((buffer) => new Uint8Array(buffer))
      .catch((error: unknown) => {
        // Do not cache a failure: let the next download try again
        logoDataPromise = null;
        throw error;
      });
  }

  return logoDataPromise;
}

function buildLogoHeader(logoData: Uint8Array): Header {
  return new Header({
    children: [
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 120 },
        children: [
          new ImageRun({
            type: "png",
            data: logoData,
            transformation: {
              width: LOGO_WIDTH,
              height: LOGO_HEIGHT,
            },
          }),
        ],
      }),
    ],
  });
}

const CELL_BORDER = {
  top: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
  bottom: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
  left: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
  right: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
};

const CELL_MARGINS = { top: 80, bottom: 80, left: 120, right: 120 };

/** Hanging indent used for (a)/(b)/(c) list lines in the Porto template. */
const LIST_INDENT_TWIPS = 680;

const LIST_ITEM_PATTERN = /^\([a-z]\)\s*\t?\s*/i;
const BULLET_PATTERN = /^•\s+/;

/** Sections with short lines that Word stretches when justified. */
const LEFT_ALIGN_SECTIONS = new Set([
  "property",
  "totalCommitments",
  "extension",
  "acquisitionFacility",
  "interestRate",
  "availabilityPeriod",
  "financialCovenants",
  "cashTrap",
]);

function textRun(
  text: string,
  options: { bold?: boolean; size?: number } = {},
): TextRun {
  return new TextRun({
    text,
    font: FONT,
    size: options.size ?? SIZE_BODY,
    bold: options.bold,
  });
}

/** Same behaviour as the preview's white-space: pre-wrap — one paragraph, \n = line break. */
function preWrapParagraph(
  text: string,
  options: {
    bold?: boolean;
    size?: number;
    alignment?: (typeof AlignmentType)[keyof typeof AlignmentType];
    spacingBefore?: number;
    spacingAfter?: number;
  } = {},
): Paragraph {
  const lines = text.split("\n");
  const children: TextRun[] = [];

  lines.forEach((line, index) => {
    if (index > 0) {
      children.push(new TextRun({ break: 1 }));
    }
    children.push(textRun(line, { bold: options.bold, size: options.size }));
  });

  return new Paragraph({
    alignment: options.alignment,
    spacing: {
      before: options.spacingBefore ?? 0,
      after: options.spacingAfter ?? 0,
      line: 240,
    },
    children,
  });
}

type ContentSegment =
  | { kind: "prose"; text: string; blockStart?: boolean }
  | { kind: "list-item"; text: string }
  | { kind: "sub-line"; text: string };

const YEAR_VALUE_LINE_PATTERN =
  /^(\[[^\]]+\]|[\d.]+%|[\d.]+x)\s+in\s+year[\s_]\d+/i;

function isListItemLine(line: string): boolean {
  const trimmed = line.trim();
  return LIST_ITEM_PATTERN.test(trimmed) || BULLET_PATTERN.test(trimmed);
}

function isYearValueLine(line: string): boolean {
  return YEAR_VALUE_LINE_PATTERN.test(line.trim());
}

function parseBlockLines(block: string): ContentSegment[] {
  const lines = block
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length === 0) {
    return [];
  }

  if (lines.length > 1 && lines.slice(1).every(isYearValueLine)) {
    return [
      { kind: "prose", text: lines[0] },
      ...lines.slice(1).map((line) => ({ kind: "sub-line" as const, text: line })),
    ];
  }

  const segments: ContentSegment[] = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];

    if (isListItemLine(line)) {
      segments.push({ kind: "list-item", text: line });
      index += 1;
      continue;
    }

    const proseLines = [line];
    index += 1;

    while (index < lines.length) {
      const nextLine = lines[index];
      if (isListItemLine(nextLine)) {
        break;
      }
      proseLines.push(nextLine);
      index += 1;
    }

    segments.push({ kind: "prose", text: proseLines.join("\n") });
  }

  return segments;
}

function parseContentSegments(text: string): ContentSegment[] {
  const segments: ContentSegment[] = [];
  const blocks = text.split(/\n\n+/).map((block) => block.trim()).filter(Boolean);

  blocks.forEach((block, blockIndex) => {
    const blockSegments = parseBlockLines(block);
    if (blockIndex > 0 && blockSegments.length > 0) {
      const first = blockSegments[0];
      if (first.kind === "prose") {
        blockSegments[0] = { ...first, blockStart: true };
      }
    }
    segments.push(...blockSegments);
  });

  return segments;
}

function buildCellParagraphs(
  text: string,
  options: { leftAlign?: boolean } = {},
): Paragraph[] {
  const proseAlignment = options.leftAlign
    ? AlignmentType.LEFT
    : AlignmentType.JUSTIFIED;

  const segments = parseContentSegments(text);
  if (segments.length === 0) {
    return [
      preWrapParagraph(text, {
        alignment: proseAlignment,
      }),
    ];
  }

  return segments.map((segment, index) => {
    const previous = segments[index - 1];
    const next = segments[index + 1];

    if (segment.kind === "list-item") {
      return new Paragraph({
        alignment: AlignmentType.LEFT,
        indent: { left: LIST_INDENT_TWIPS, hanging: LIST_INDENT_TWIPS },
        spacing: { before: 0, after: 0, line: 240 },
        children: [textRun(segment.text.replace(BULLET_PATTERN, ""))],
      });
    }

    if (segment.kind === "sub-line") {
      return new Paragraph({
        alignment: AlignmentType.LEFT,
        spacing: { before: 0, after: 0, line: 240 },
        children: [textRun(segment.text)],
      });
    }

    const spacingBefore =
      segment.blockStart
        ? 120
        : previous?.kind === "list-item"
          ? 120
          : 0;

    return preWrapParagraph(segment.text, {
      alignment: proseAlignment,
      spacingBefore,
      spacingAfter: next?.kind === "list-item" ? 120 : 0,
    });
  });
}

function tableCell(
  text: string,
  widthPercent: number,
  options: { bold?: boolean; justified?: boolean; leftAlign?: boolean } = {},
): TableCell {
  const children = options.bold
    ? [preWrapParagraph(text, { bold: true })]
    : buildCellParagraphs(text, {
        leftAlign: options.leftAlign ?? false,
      });

  return new TableCell({
    width: { size: widthPercent, type: WidthType.PERCENTAGE },
    verticalAlign: VerticalAlign.TOP,
    borders: CELL_BORDER,
    margins: CELL_MARGINS,
    children,
  });
}

function buildTermsTable(sections: TermSheetSectionRow[]): Table {
  const rows = getVisibleTermSheetSections(sections).map(
    (section) =>
      new TableRow({
        children: [
          tableCell(formatTermSheetLabel(section.label), 25, { bold: true }),
          tableCell(section.content, 75, {
            leftAlign: LEFT_ALIGN_SECTIONS.has(section.id),
            justified: !LEFT_ALIGN_SECTIONS.has(section.id),
          }),
        ],
      }),
  );

  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows,
  });
}

function buildClosingDisclaimerParagraphs(text: string): Paragraph[] {
  if (!text.trim()) {
    return [];
  }

  const paragraphs: Paragraph[] = [];
  let remainder = text.trim();

  if (remainder.startsWith("DISCLAIMER")) {
    paragraphs.push(
      new Paragraph({
        spacing: { before: 200, after: 120 },
        children: [textRun("DISCLAIMER", { bold: true })],
      }),
    );
    remainder = remainder.slice("DISCLAIMER".length).replace(/^\n+/, "");
  }

  for (const block of remainder.split(/\n\n+/)) {
    const trimmed = block.trim();
    if (!trimmed) {
      continue;
    }

    paragraphs.push(
      preWrapParagraph(trimmed, {
        alignment: AlignmentType.JUSTIFIED,
        spacingAfter: 120,
      }),
    );
  }

  return paragraphs;
}

export async function downloadTermSheetDocx(options: {
  title: string;
  date: string;
  sections: TermSheetSectionRow[];
  closingDisclaimer: string;
  dealName: string | null | undefined;
}) {
  const { title, date, sections, closingDisclaimer, dealName } = options;
  const logoData = await loadTenfiftyLogoData();

  const wordDocument = new Document({
    sections: [
      {
        properties: {
          titlePage: true,
        },
        headers: {
          first: buildLogoHeader(logoData),
        },
        children: [
          preWrapParagraph(title, {
            bold: true,
            size: SIZE_TITLE,
            alignment: AlignmentType.CENTER,
            spacingAfter: 120,
          }),
          preWrapParagraph(date, {
            size: SIZE_TITLE,
            alignment: AlignmentType.CENTER,
            spacingAfter: 120,
          }),
          new Paragraph({
            alignment: AlignmentType.JUSTIFIED,
            spacing: { after: 200 },
            children: [
              textRun("DISCLAIMER:", { bold: true }),
              textRun(` ${TERM_SHEET_DISCLAIMER}`),
            ],
          }),
          buildTermsTable(sections),
          ...buildClosingDisclaimerParagraphs(closingDisclaimer),
        ],
      },
    ],
  });

  const blob = await Packer.toBlob(wordDocument);
  const url = window.URL.createObjectURL(blob);
  const link = window.document.createElement("a");

  link.href = url;
  link.download = toTermSheetDownloadFilename(dealName);
  window.document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}
